// src/pages/legal/Compliance.js
import React from 'react'
import { Box, Container, Typography, Card, CardContent, Divider, Stack } from '@mui/material'
import { useLanguage } from '../../contexts/LanguageContext'

export default function Compliance() {
    const effectiveDate = 'January 5, 2026' // change when needed
    const supportEmail = 'support@mojopayr.com' // change when needed

    const { t, lang } = useLanguage()
    const isFr = lang === 'fr'
    const tt = (key, enFallback, frFallback) => t(key, null, isFr ? frFallback : enFallback)

    const sectionTitleSx = {
        fontWeight: 900,
        color: 'rgba(255,255,255,0.92)',
        mt: 2.4,
        mb: 0.8,
        letterSpacing: 0.2,
    }

    const bodySx = {
        color: 'rgba(255,255,255,0.78)',
        lineHeight: 1.9,
        fontSize: { xs: '0.98rem', md: '1.02rem' },
        wordBreak: 'break-word',
        overflowWrap: 'anywhere',
    }

    const liSx = { mb: 0.8 }

    return (
        <Box sx={{ py: { xs: 5, md: 7 } }}>
            <Container maxWidth="md">
                <Typography variant="h4" sx={{ fontWeight: 950, color: 'white', mb: 1 }}>
                    {tt('legal.compliance.title', 'Compliance', 'Conformité')}
                </Typography>

                <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={1}
                    sx={{ mb: 3, alignItems: { sm: 'center' } }}
                >
                    <Typography sx={{ color: 'rgba(255,255,255,0.72)' }}>
                        {tt(
                            'legal.compliance.subtitle',
                            'How MojoPayr meets regulatory and operational standards.',
                            'Comment MojoPayr respecte les normes réglementaires et opérationnelles.'
                        )}
                    </Typography>

                    <Typography sx={{ color: 'rgba(255,255,255,0.55)' }}>
                        {tt('legal.compliance.effective', 'Effective Date:', 'Date d’effet :')} {effectiveDate}
                    </Typography>
                </Stack>

                <Card
                    sx={{
                        borderRadius: 6,
                        background: 'rgba(255,255,255,0.06)',
                        border: '1px solid rgba(255,255,255,0.10)',
                        backdropFilter: 'blur(10px)',
                    }}
                >
                    <CardContent sx={{ p: { xs: 2.4, md: 3.2 } }}>
                        <Typography sx={bodySx}>
                            {tt(
                                'legal.compliance.intro',
                                'MojoPayr is committed to operating responsibly and meeting applicable compliance requirements. This page explains the controls we use to reduce fraud risk, support safe transactions, and meet legal obligations where the Service operates.',
                                "MojoPayr s’engage à opérer de manière responsable et à respecter les exigences de conformité applicables. Cette page explique les contrôles que nous utilisons pour réduire le risque de fraude, soutenir des transactions sûres et respecter les obligations légales là où le Service est disponible."
                            )}
                        </Typography>

                        <Divider sx={{ my: 2.2, borderColor: 'rgba(255,255,255,0.10)' }} />

                        <Typography sx={sectionTitleSx}>
                            {tt('legal.compliance.s1', '1. Anti-Money Laundering Program', '1. Programme anti-blanchiment (AML)')}
                        </Typography>
                        <Typography sx={bodySx}>
                            {tt(
                                'legal.compliance.s1.p1',
                                'We maintain an anti-money laundering (AML) program designed to detect and prevent misuse of the Service. Our program includes:',
                                "Nous maintenons un programme anti-blanchiment (AML) conçu pour détecter et prévenir l’utilisation abusive du Service. Notre programme comprend :"
                            )}
                        </Typography>

                        <Box component="ul" sx={{ mt: 1.2, mb: 0, pl: 2.2, ...bodySx }}>
                            <li style={liSx}>{tt('legal.compliance.s1.li1', 'Customer identity verification (KYC)', 'Vérification de l’identité client (KYC)')}</li>
                            <li style={liSx}>{tt('legal.compliance.s1.li2', 'Transaction monitoring and risk review', 'Surveillance des transactions et revue des risques')}</li>
                            <li style={liSx}>{tt('legal.compliance.s1.li3', 'Recordkeeping to meet legal requirements', 'Tenue de registres pour respecter les exigences légales')}</li>
                            <li style={liSx}>{tt('legal.compliance.s1.li4', 'Internal controls and periodic reviews', 'Contrôles internes et revues périodiques')}</li>
                        </Box>

                        <Typography sx={sectionTitleSx}>
                            {tt('legal.compliance.s2', '2. Sanctions and Watchlist Screening', '2. Filtrage sanctions et listes de surveillance')}
                        </Typography>
                        <Typography sx={bodySx}>
                            {tt(
                                'legal.compliance.s2.p1',
                                'Where required, we screen customers and transactions against relevant sanctions and watchlists. If a match is detected, we may restrict activity, request more information, or take other steps required by law.',
                                "Lorsque requis, nous contrôlons les clients et les transactions par rapport aux sanctions et listes de surveillance pertinentes. En cas de correspondance, nous pouvons limiter l’activité, demander plus d’informations ou prendre d’autres mesures exigées par la loi."
                            )}
                        </Typography>

                        <Typography sx={sectionTitleSx}>
                            {tt('legal.compliance.s3', '3. Fraud Prevention', '3. Prévention de la fraude')}
                        </Typography>
                        <Typography sx={bodySx}>
                            {tt(
                                'legal.compliance.s3.p1',
                                'We use security and risk controls to help reduce fraud and unauthorized activity. These may include:',
                                "Nous utilisons des contrôles de sécurité et de risque pour réduire la fraude et les activités non autorisées. Cela peut inclure :"
                            )}
                        </Typography>

                        <Box component="ul" sx={{ mt: 1.2, mb: 0, pl: 2.2, ...bodySx }}>
                            <li style={liSx}>{tt('legal.compliance.s3.li1', 'Account security checks and login protections', 'Contrôles de sécurité du compte et protections de connexion')}</li>
                            <li style={liSx}>{tt('legal.compliance.s3.li2', 'Device and session risk signals', 'Signaux de risque liés à l’appareil et à la session')}</li>
                            <li style={liSx}>{tt('legal.compliance.s3.li3', 'Velocity limits and behavior monitoring', 'Limites de vitesse et surveillance des comportements')}</li>
                            <li style={liSx}>{tt('legal.compliance.s3.li4', 'Manual review for higher-risk activity', 'Revue manuelle des activités à risque élevé')}</li>
                        </Box>

                        <Typography sx={sectionTitleSx}>
                            {tt('legal.compliance.s4', '4. Data Security and Privacy Controls', '4. Sécurité des données et contrôles de confidentialité')}
                        </Typography>
                        <Typography sx={bodySx}>
                            {tt(
                                'legal.compliance.s4.p1',
                                'We apply security controls to help protect customer information. These may include encryption, access controls, monitoring, and secure storage practices. For details on data handling, refer to our Privacy Policy.',
                                "Nous appliquons des contrôles de sécurité pour protéger les informations des clients. Cela peut inclure le chiffrement, les contrôles d’accès, la surveillance et des pratiques de stockage sécurisé. Pour les détails, consultez notre Politique de confidentialité."
                            )}
                        </Typography>

                        <Typography sx={sectionTitleSx}>
                            {tt('legal.compliance.s5', '5. Recordkeeping and Audits', '5. Tenue de registres et audits')}
                        </Typography>
                        <Typography sx={bodySx}>
                            {tt(
                                'legal.compliance.s5.p1',
                                'We keep records related to identity verification and transactions as required by applicable laws. We may also perform internal reviews and audits to confirm controls are working as intended.',
                                "Nous conservons les enregistrements liés à la vérification d’identité et aux transactions, conformément aux lois applicables. Nous pouvons aussi effectuer des revues internes et des audits pour vérifier que les contrôles fonctionnent comme prévu."
                            )}
                        </Typography>

                        <Typography sx={sectionTitleSx}>
                            {tt('legal.compliance.s6', '6. Reporting and Lawful Requests', '6. Signalement et demandes légales')}
                        </Typography>
                        <Typography sx={bodySx}>
                            {tt(
                                'legal.compliance.s6.p1',
                                'We may cooperate with regulators and respond to lawful requests from authorities. Where required, we may report suspicious activity and take steps to restrict or freeze transactions.',
                                "Nous pouvons coopérer avec les régulateurs et répondre aux demandes légales des autorités. Lorsque requis, nous pouvons signaler des activités suspectes et prendre des mesures pour limiter ou geler des transactions."
                            )}
                        </Typography>

                        <Typography sx={sectionTitleSx}>
                            {tt('legal.compliance.s7', '7. Customer Responsibilities', '7. Responsabilités du client')}
                        </Typography>
                        <Typography sx={bodySx}>
                            {tt(
                                'legal.compliance.s7.p1',
                                'You agree to use the Service only for lawful purposes and to provide accurate information. Attempts to misuse the Service may result in account restrictions or closure.',
                                "Vous acceptez d’utiliser le Service uniquement à des fins légales et de fournir des informations exactes. Toute tentative d’utilisation abusive peut entraîner des restrictions ou la fermeture du compte."
                            )}
                        </Typography>

                        <Divider sx={{ my: 2.2, borderColor: 'rgba(255,255,255,0.10)' }} />

                        <Typography sx={sectionTitleSx}>
                            {tt('legal.compliance.s8', '8. Contact', '8. Contact')}
                        </Typography>
                        <Typography sx={bodySx}>
                            {tt('legal.compliance.s8.p1', 'For compliance questions, contact us at:', 'Pour les questions de conformité, contactez-nous à :')}
                        </Typography>

                        <Typography sx={{ ...bodySx, mt: 1 }}>
                            {tt('legal.compliance.emailLabel', 'Email:', 'E-mail :')}{' '}
                            <Box
                                component="a"
                                href={`mailto:${supportEmail}`}
                                sx={{
                                    color: 'rgba(255,255,255,0.92)',
                                    textDecoration: 'underline',
                                    textUnderlineOffset: '3px',
                                }}
                            >
                                {supportEmail}
                            </Box>
                        </Typography>
                    </CardContent>
                </Card>
            </Container>
        </Box>
    )
}

